import { create } from 'zustand'

/**
 * The web app's install prompt, held once for the whole app.
 *
 * `beforeinstallprompt` fires once per page load, early — often before any
 * page has mounted — and the event it carries can be prompted exactly once.
 * A component that listens for it in its own effect either misses it (the
 * download page, reached by navigation after it fired) or ends up holding a
 * copy another component has already spent (the header banner). So it is
 * captured here, at module load, and the banner and the download page read
 * the same one: whichever of them prompts, the other sees it gone.
 */

export function isStandalone() {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari's own flag — display-mode media query support there is
    // inconsistent across versions, so this is checked in addition, not
    // instead of, the standard query above.
    window.navigator.standalone === true
  )
}

export function isIos() {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

export const useInstallStore = create((set, get) => ({
  /** The deferred `beforeinstallprompt` event, or null when the browser has not offered one — yet, or at all. */
  deferredEvent: null,
  /** True once `appinstalled` fires, from whichever prompt the learner accepted. */
  installed: false,

  /**
   * Show the browser's own install dialog. Resolves to 'accepted' or
   * 'dismissed', or null when there was nothing to show.
   */
  async prompt() {
    const event = get().deferredEvent
    if (!event) return null
    // The event can be prompted once; drop it before awaiting, so a second
    // tap while the dialog is up finds nothing to spend.
    set({ deferredEvent: null })
    await event.prompt()
    const choice = await event.userChoice
    return choice?.outcome ?? null
  },
}))

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep the browser from showing its own mini-infobar on its schedule;
    // the app decides where the prompt appears (the banner, the download page).
    event.preventDefault()
    useInstallStore.setState({ deferredEvent: event })
  })
  window.addEventListener('appinstalled', () => {
    useInstallStore.setState({ deferredEvent: null, installed: true })
  })
}
